import { useT } from "@transifex/react";
import SidneyCard from "../../basic/SidneyCard";
import { LOGIN_COLUMN_CLASS, PRIMARY_PILL_BUTTON_CLASS, SECONDARY_PILL_BUTTON_CLASS } from "./classMemberFormStyles";

interface WelcomeCodeProps {
    loginCode: string;
    onDone: () => void;
    /** Offers to show the code again after the next submitted test; omitted when the code is already being shown again. */
    onShowLater?: () => void;
}

/** `ABCDEFGH` → `AB CD EF GH`: pairs are easier to copy by hand. */
const formatLoginCode = (code: string) => code.match(/.{1,2}/g)?.join(" ") ?? code;

const WelcomeCode = (props: WelcomeCodeProps) => {
    const t = useT();

    const t_title = t("Welcome to the classroom!", { _tags: "ClassMemberLogin, WelcomeCode" });
    const t_useThisCode = t("From now on you can log in with this code:", { _tags: "ClassMemberLogin, WelcomeCode" });
    const t_writeItDown = t("Best write it down.", { _tags: "ClassMemberLogin, WelcomeCode" });
    const t_noted = t("Noted!", { _tags: "ClassMemberLogin, WelcomeCode" });
    const t_showLater = t("Show it to me again later", { _tags: "ClassMemberLogin, WelcomeCode" });
    const t_askTeacher = t("If you ever forget it, ask your teacher.", { _tags: "ClassMemberLogin, WelcomeCode" });

    return (
        <div
            className={`${LOGIN_COLUMN_CLASS} gap-m`}
            data-testid="welcome-code"
        >
            <div className="flex w-full flex-col items-center gap-xs text-center">
                <h1 className="text-xxl font-semibold text-p6-grey-dark">{t_title}</h1>
                <p className="text-m text-p6-grey-dark">{t_useThisCode}</p>
            </div>
            <SidneyCard>
                <span
                    className="text-xxl font-semibold tracking-widest text-p6-grey-dark"
                    data-testid="welcome-code-value"
                >
                    {formatLoginCode(props.loginCode)}
                </span>
                <span className="text-m text-p6-grey-dark">{t_writeItDown}</span>
            </SidneyCard>
            <div className="flex w-full flex-col items-center gap-s">
                <button
                    type="button"
                    onClick={props.onDone}
                    data-testid="button-welcome-code-done"
                    className={PRIMARY_PILL_BUTTON_CLASS}
                >
                    {t_noted}
                </button>
                {props.onShowLater && (
                    <button
                        type="button"
                        onClick={props.onShowLater}
                        data-testid="button-welcome-code-show-later"
                        className={SECONDARY_PILL_BUTTON_CLASS}
                    >
                        {t_showLater}
                    </button>
                )}
                <p className="text-m text-p6-grey-mid text-center">{t_askTeacher}</p>
            </div>
        </div>
    );
};

export default WelcomeCode;
